import {
  DialogComponent,
  DialogOpenEvent,
  DialogCloseEvent,
} from "@theme/dialog";
import { CartAddEvent } from "@theme/events";
import { isMobileBreakpoint } from "@theme/utilities";

const TOAST_HIDE_MS = 4000;
/** @type {ReturnType<typeof setTimeout> | undefined} */
let toastHideTimer;
/** @type {HTMLElement | null} */
let cartToast = null;

function getCartToast() {
  if (!cartToast?.isConnected) {
    const toasts = document.querySelectorAll("[data-escape-cart-toast]");
    cartToast = toasts[0] || null;
    if (!cartToast) return null;
    for (const extra of toasts) {
      if (extra !== cartToast) extra.remove();
    }
  }

  // Morph can pull this back into the header; keep it on body.
  if (cartToast.parentElement !== document.body) {
    document.body.appendChild(cartToast);
  }
  return cartToast;
}

function hideCartToast() {
  const toast = getCartToast();
  if (!toast) return;
  delete toast.dataset.open;
  clearTimeout(toastHideTimer);
}

/** GoKwik Cart Drawer sets this once side-cart.js has loaded. */
function openGokwikCart() {
  document.querySelector("cart-drawer-component")?.close?.();
  if (typeof window.escapeOpenGokwikCart === "function") {
    window.escapeOpenGokwikCart();
    openGokwikCart.tries = 0;
    return true;
  }
  if (typeof window.openGokwikSideCart === "function") {
    window.openGokwikSideCart();
    openGokwikCart.tries = 0;
    return true;
  }
  // ponytail: side-cart.js is deferred; give up after ~2s
  openGokwikCart.tries = (openGokwikCart.tries || 0) + 1;
  if (openGokwikCart.tries > 40) return false;
  clearTimeout(openGokwikCart.timer);
  openGokwikCart.timer = setTimeout(openGokwikCart, 50);
  return true;
}

function showCartToast() {
  const toast = getCartToast();
  if (!toast) return;

  // Drop copies morph puts back in the header after cart:update.
  for (const extra of document.querySelectorAll("[data-escape-cart-toast]")) {
    if (extra !== toast) extra.remove();
  }

  toast.hidden = false;
  delete toast.dataset.open;
  // ponytail: reflow so the slide-in restarts when adding again
  toast.offsetWidth;
  toast.dataset.open = "true";
  clearTimeout(toastHideTimer);
  toastHideTimer = setTimeout(hideCartToast, TOAST_HIDE_MS);
  // ponytail: header morph after cart:update can yank the node; re-pin shortly after
  requestAnimationFrame(() => getCartToast());
  setTimeout(getCartToast, 100);
}

document.addEventListener("click", (event) => {
  const go = event.target.closest("[data-escape-cart-toast-go]");
  if (!go) return;
  hideCartToast();
  if (openGokwikCart()) return;
  const drawer = document.querySelector("cart-drawer-component");
  if (drawer && typeof drawer.open === "function") {
    drawer.open();
    return;
  }
  window.location.href = Theme.routes?.cart_url || "/cart";
});

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, hideCartToast);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, "");
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#updateStickyState,
    );
    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#handleHistoryOpen,
    );
    this.removeEventListener(DialogOpenEvent.eventName, hideCartToast);
    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleHistoryClose,
    );
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, "");
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener("popstate", this.#handlePopState, {
      signal: this.#historyAbortController.signal,
    });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty(
        "--dialog-drawer-closing-animation",
        "none",
      );
      await this.closeDialog();
      this.refs.dialog.style.removeProperty(
        "--dialog-drawer-closing-animation",
      );
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    const source = event.detail?.data?.source;
    const userAdd =
      source === "product-form-component" ||
      source === "escape-cart-recommendations" ||
      source === "quick-order-quantity";
    if (userAdd && !event.detail?.data?.didError) openGokwikCart();
    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /** Theme drawer stays closed. GoKwik Cart draws the panel. */
  showDialog() {
    openGokwikCart();
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (
      this.refs.liveRegion
    );
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined)
      return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    if (openGokwikCart()) return;
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined("shopify-payment-terms").then(() => {
      const installmentsContent = document.querySelector(
        "shopify-payment-terms",
      )?.shadowRoot;
      const cta = installmentsContent?.querySelector(
        "#shopify-installments-cta",
      );
      cta?.addEventListener("click", this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector(".cart-drawer__content");
    const summary = dialog.querySelector(".cart-drawer__summary");

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute("cart-summary-sticky", "false");
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute(
      "cart-summary-sticky",
      ratio > this.#summaryThreshold ? "false" : "true",
    );
  }
}

if (!customElements.get("cart-drawer-component")) {
  customElements.define("cart-drawer-component", CartDrawerComponent);
}
